/**
 * Analytics-safe descriptions of *why* a waitlist submit failed.
 *
 * Only field names + error codes ever leave the browser — never the values the
 * visitor typed. See src/lib/analytics.ts for the PII contract.
 */

export type FieldIssue = { path: PropertyKey[]; code: string; message?: string };

/** Maps one Zod issue to a coarse, non-identifying reason. */
export function issueCode(issue: FieldIssue, value?: unknown): string {
  const isEmpty = typeof value === "string" && value.trim() === "";
  switch (issue.code) {
    case "invalid_type":
      return "missing";
    case "too_small":
      return isEmpty ? "missing" : "too_short";
    case "too_big":
      return "too_long";
    case "invalid_string":
    case "invalid_format":
    case "custom":
      return isEmpty ? "missing" : "invalid";
    default:
      return isEmpty ? "missing" : "invalid";
  }
}

/**
 * "email:invalid,firstName:missing" — stable, sortable, safe to chart.
 * One entry per field (first issue wins), ordered by field name.
 */
export function errorCodes(issues: FieldIssue[], values: Record<string, unknown> = {}): string[] {
  const byField = new Map<string, string>();
  for (const issue of issues) {
    const field = String(issue.path[0] ?? "form");
    if (byField.has(field)) continue;
    byField.set(field, issueCode(issue, values[field]));
  }
  return [...byField.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([f, c]) => `${f}:${c}`);
}

/** Coarse seniority bucket for a recognized duplicate — never the exact number. */
export function seniorityBucket(pilotNo: number | null | undefined): string {
  if (pilotNo == null) return "unknown";
  if (pilotNo <= 10) return "1-10";
  if (pilotNo <= 50) return "11-50";
  if (pilotNo <= 100) return "51-100";
  if (pilotNo <= 500) return "101-500";
  return "500+";
}

/** Coarse device class, derived from viewport width (no UA parsing). */
export function deviceType(): string {
  if (typeof window === "undefined") return "unknown";
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}
